import { cloneDeep, uniqBy, isNil } from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import {FREE_MOVEMENT_OF_PERSONS_COUNTRIES} from '@/config/constants'
import { LogHelper } from '@/helpers/Log'
import {DateTimeUtil} from "@/utils/DateTime";
import {ProductUtil} from "@/utils/Product";
import {isRequired, MissingParameterError, NotFoundError} from "@/utils/CustomErrors";

export const BasketUtil = {
  /**
   * Add Doctor from Product to basket
   *
   * @param {Basket} basket   Current basket
   * @param {Doctor} doctor   Doctor-Object
   * @return {Promise<Basket>} Returns basket with added/transfered doctors
   */
  addPersonDoctor(basket, productDoctor) {
    const index = basket.doctors.findIndex(d => d.zsrNumber === productDoctor.zsrNumber)
    if (index === -1) {
      const doctor = BasketUtil.transferProductDoctor(productDoctor)
      basket.doctors.push(doctor)
    }
    return Promise.resolve(basket)
  },

  /**
   * Checks, if one of the flaged fields have changed
   * Checks for: address, contractStartDate, dateOfBirth, gender and collectivePersonGroup
   * @param {Basket} updated
   * @param {Basket} original
   *
   * @returns {Boolean}
   */
  checkNeedsConfirmation(updated, original) {
    if (updated.address !== original.address) return true
    if (updated.contractStartDate !== original.contractStartDate) return true
    if (updated.persons.length !== original.persons.length) return true

    return updated.persons.some(
      (person, index) =>
        person.personData.dateOfBirth !== original.persons[index].personData.dateOfBirth ||
        person.personData.gender !== original.persons[index].personData.gender ||
        person.collectivePersonGroup !== original.persons[index].collectivePersonGroup
    )
  },

  /**
   * Returns all the productIds of the selected products with caveats for a person
   * @param person
   * @param contractPerson
   * @return []
   */
  getCaveatsForPerson(person, contractPerson) {
    if (!person?.products?.products || !contractPerson?.products?.products) return []
    const personProducts = person.products.products
    const contractProducts = contractPerson.products.products

    return Object.keys(personProducts)
      .filter(p => {
        const cp = contractProducts[p]
        return cp && personProducts[p].selected && personProducts[p].conditionalProduct
      })
  },

  getPersonsForExistingCustomers(basketPersons, contractPersons) {
    const getContract = person => contractPersons.find(p => p.partnerNumber === person.partnerNumber)

    const mapExistingContractDataToPerson = person => {
      const existingPerson = getContract(person)
      if (!existingPerson) {
        throw new NotFoundError('contract for person', person.partnerNumber)
      }
      return Object.assign({}, person, {
        products: existingPerson.products,
        selectedProducts: existingPerson.selectedProducts,
        total: existingPerson.total,
      })
    }

    return basketPersons
      // Only show persons:
      //  - which have an existing contract
      //  - which don't have an existing contract, but have been created/modified with this offer
      .filter(basketPerson => !!getContract(basketPerson) || (!getContract(basketPerson) && basketPerson.contractModified))
      .map(basketPerson => !basketPerson.contractModified ? mapExistingContractDataToPerson(basketPerson) : basketPerson)
  },

  /**
   * isStaticBasket returns true for baskets of existing customers with closed products in the contract
   * @param basket
   * @returns {boolean}
   */
  isStaticBasket(basket) {
    if (!basket || basket.existingCustomer === undefined || basket.dynamic === undefined) return false
    return basket.existingCustomer && !basket.dynamic
  },

  /**
   * Cleanup basket of unused doctors (not linked to product or hq)
   *
   * @param {Basket} basket       Current basket
   * @returns {Promise<Basket>}   Returns basket with only used doctors
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  removeUnusedDoctors(basket) {
    const updatedBasket = cloneDeep(basket)
    if (updatedBasket?.doctors?.length > 0) {
      const basketDoctors = updatedBasket.doctors.reduce((doctors, doctor) => {
        if (!doctor.zsrNumber) {
          doctors.push(doctor)
        } else {
          updatedBasket.persons.some(person => {
            if (!person.products) {
              return false
            }

            const products = Object.values(person.products.products)

            if (products.some(product => product.doctor?.zsrNumber === doctor.zsrNumber && product.selected)) {
              return doctors.push(doctor)
            }

            person.healthQuestionAnswers.some(question => {
              if (question.doctorIds?.length > 0 && question.doctorIds.includes(doctor.doctorId)) {
                return doctors.push(doctor)
              } else if (question?.treatmentCases?.length > 0) {
                return question.treatmentCases.some(treatment => {
                  if (treatment.doctorIds?.length > 0 && treatment.doctorIds.includes(doctor.doctorId)) {
                    return doctors.push(doctor)
                  }
                })
              }
            })
          })
        }
        return doctors
      }, [])

      updatedBasket.doctors = uniqBy(basketDoctors, 'doctorId')
    }
    return Promise.resolve(basket)
  },

  /**
   * @param {Person[]} persons
   * @returns {Number}
   */
  getTotal(persons) {
    // TODO: Can parameter be undefined?
    return persons.reduce((acc, value) => {
      if (isNil(value.total)) {
        LogHelper.error(`person ${value.personId} has no total`)
      }
      acc += value.total
      return acc
    }, 0)
  },

  transferProductDoctor(doctor) {
    const { address } = doctor
    return {
      country: address.country,
      doctorId: uuidv4(),
      name: [doctor.firstName, doctor.lastName].join(' ').trim(),
      street: [address.street, address.houseNumber].join(' ').trim(),
      town: [address.zipCode, address.town].join(' ').trim(),
      zsrNumber: doctor.zsrNumber,
    }
  },

  hasPersonsNeedingPermit(basketPersons) {
    return basketPersons.some(person => {
      const movedToCH = person?.preInsurer?.currentKvgMovedToCH ?? false
      return movedToCH && !FREE_MOVEMENT_OF_PERSONS_COUNTRIES.includes(person.preInsurer.currentMovedToCHType)
    })
  },

  hasPersons(basket) {
    return !!basket.persons?.length
  },

  /**
   * Returns a person object with the calculated age
   * @param persons
   * @param personId
   * @return {{}}
   */
  getPersonWithAge(persons = isRequired('persons'), personId = isRequired('personId')) {
    const personsCopy = cloneDeep(persons)
    const person = personsCopy.find(p => p.personId === personId)

    if (!person) throw new NotFoundError('person with personId', personId)

    person.age = DateTimeUtil.getAge(person.personData.dateOfBirth)
    return person
  },

  /**
   * Returns the total of all the selected products
   * @param products
   * @return number
   */
  getTotalPriceOfPerson(products = isRequired('products')) {
    return products.reduce((total, product) => total + (product.price?.price || 0), 0)
  },

  /**
   * Returns the recommended products of a person
   * @param productData
   * @param person
   * @return []
   */
  getRecommendations(productData, person) {
    if (!person) throw new MissingParameterError('person')
    if (!person.recommendations) throw new MissingParameterError('recommendations')
    if (!person.products?.products) throw new MissingParameterError('products')
    return person.recommendations
      .map(id => {
        const categoryId = ProductUtil.getCategoryIdFromProduct(productData, id)
        return Object.assign(
          {id, categoryId},
          person.products.products[id]
        )
      })
  },

  selectProducts (personId, basket, fromProductIds, toProducts){
    const pBasket = basket.persons.find(p => p.personId === personId)

    fromProductIds.forEach(productId => {
      // deselect old products
      pBasket.products.products[productId].selected = false
    })

    toProducts.forEach(product => {
      // select new product
      pBasket.products.products[product.id].selected = true

      if (product.doctor) {
        pBasket.products.products[product.id].doctor = product.doctor
      }

      // set default-criteria
      if (product.criteria) {

        pBasket.products.products[product.id].prices.map(p => {
          const crit = product.criteria

          const needsFranchiseCheck = crit.value !== undefined && p.value !== undefined
          const needsDurationCheck = crit.duration !== undefined && p.duration !== undefined
          const needsAccidentCheck = false // crit.toggle !== undefined && p.toggle !== undefined

          let isMatch = true

          if (needsFranchiseCheck && crit.value !== p.value) isMatch = false
          if (needsDurationCheck && crit.duration !== p.duration) isMatch = false
          // no check necessary, because toggle is inherited
          // if (needsAccidentCheck && crit.toggle !== p.toggle) isMatch = false

          // apply accident-value to all (applicable) criteria-selections
          if (p.toggle !== undefined) p.toggle = crit.toggle

          if ((needsFranchiseCheck || needsDurationCheck || needsAccidentCheck) && isMatch) {
            p.selected = true
          } else if ((!needsFranchiseCheck && !needsDurationCheck && !needsAccidentCheck)) {
            // don't do nothing, man!
          } else {
            p.selected = false
          }

          return p
        })
      }
    })

    return basket
  },
}
